// HDRISwitcher — manual override of scene.environment with one of the
// pre-baked equirect cubemaps in /assets/environments/equirect/.
//
// Two pieces:
//   • HDRISwitcherUI   — floating DOM dropdown, mounted outside <Canvas>.
//   • HDRIApplier      — invisible R3F component, mounted inside <Canvas>;
//                        watches the store and re-binds scene.environment.
//
// "Default (from splat)" leaves the override null so EnvProbe stays in
// control. Otherwise the picked HDRI takes precedence and EnvProbe's
// applied env is replaced until the user switches back.

import React, { useEffect, useState } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import { splatCacheKey } from '../lib/envCubemapCache'
import { mapWithConcurrency } from '../lib/mapWithConcurrency'
import { setHdriOverride, useHdriOverride } from '../lib/hdriOverrideStore'
import { getCurrentCharacterId, loadCharacters } from '../lib/characterState'
import { listEnvironments } from '../lib/environmentsApi'

// How many bake probes may be in flight at once.
//
// Deliberately well under the browser's 6-connections-per-origin budget: this
// sweep is a dropdown-population nicety racing the CHARACTER LOAD, which is what
// the user is actually waiting for, so it must never be able to take the whole
// pool. It used to be effectively 1 (a serial await loop) which is worse, not
// better — a serial sweep of N rows is N round trips long, so on a 59-row org it
// kept issuing probes for minutes and each one still held a connection for its
// whole trip. Bounded-but-parallel finishes the same work in ceil(N/4) trips
// while leaving 2+ connections free throughout.
const PROBE_CONCURRENCY = 4

// Build the option list for the active character: for each uploaded
// environment_assets row, derive the equirect cache URL via the same hash
// EnvProbe uses. HEAD-check drops options whose cubemap hasn't been baked
// yet. ENG-529: server-backed instead of bundled manifest.
async function buildOptionsForSlug(slug) {
  const opts = [{ url: null, label: 'Default (from splat)' }]
  if (!slug) return opts
  let rows
  try {
    rows = await listEnvironments(slug)
  } catch (e) {
    console.warn('[HDRISwitcher] listEnvironments failed:', e)
    return opts
  }
  const candidates = (rows ?? []).filter(env => env.fileUrl ?? env.file_url)
  // Probed with a bounded pool. Each probe resolves to an option or null — a
  // 404 means "not baked yet", which is an ANSWER, so it is data rather than
  // something to report. mapWithConcurrency preserves input order, so the
  // dropdown keeps the server's ordering however the probes interleave.
  const probed = await mapWithConcurrency(candidates, PROBE_CONCURRENCY, async (env) => {
    const splatUrl = env.fileUrl ?? env.file_url
    const key = await splatCacheKey(splatUrl)
    const equirectUrl = `/assets/environments/equirect/${key}.png`
    const head = await fetch(equirectUrl, { method: 'HEAD' }).catch(() => null)
    if (!head?.ok || !/^image\//.test(head.headers.get('content-type') ?? '')) return null
    return { url: equirectUrl, label: env.name ?? key }
  })
  for (const opt of probed) { if (opt) opts.push(opt) }
  return opts
}

export function HDRISwitcherUI({ style }) {
  const override = useHdriOverride()
  const [options, setOptions] = useState([{ url: null, label: 'Default (from splat)' }])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let slug = getCurrentCharacterId()
      if (!slug) {
        try {
          const list = await loadCharacters()
          if (cancelled) return
          slug = getCurrentCharacterId(list)
        } catch { /* ignore — falls through to no-options */ }
      }
      const opts = await buildOptionsForSlug(slug)
      if (!cancelled) setOptions(opts)
    })()
    return () => { cancelled = true }
  }, [])

  if (options.length <= 1) return null  // no baked HDRIs available — hide

  return (
    <div style={{
      position: 'absolute', top: 8, right: 8, zIndex: 10,
      background: 'rgba(12,14,21,0.85)', border: '1px solid #1f2129',
      borderRadius: 4, padding: '4px 8px',
      fontFamily: 'var(--m-font)', fontSize: 11, color: '#e1e1ed',
      pointerEvents: 'auto',
      ...style,
    }}>
      <label style={{ marginRight: 6, color: '#8f8fa2' }}>HDRI</label>
      <select
        value={override?.url ?? ''}
        onChange={e => {
          const opt = options.find(o => (o.url ?? '') === e.target.value)
          setHdriOverride(opt?.url ? opt : null)
        }}
        style={{
          background: '#080a10', color: '#e1e1ed',
          border: '1px solid #1f2129', borderRadius: 3,
          padding: '2px 4px', fontFamily: 'var(--m-font)', fontSize: 11,
        }}
      >
        {options.map(o => (
          <option key={o.url ?? 'default'} value={o.url ?? ''}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

// Inside-Canvas applier. Watches the override store and binds the chosen
// equirect PNG as an envMap (PMREM-prefiltered). When the override is
// cleared, releases the texture; EnvProbe's effect re-runs and rebinds its
// splat-derived env (if any).
export function HDRIApplier() {
  const gl = useThree(s => s.gl)
  const scene = useThree(s => s.scene)
  const override = useHdriOverride()

  useEffect(() => {
    if (!override?.url) return
    let cancelled = false
    let envTexture = null
    const pmrem = new THREE.PMREMGenerator(gl)
    pmrem.compileEquirectangularShader()

    const loader = new THREE.TextureLoader()
    loader.load(
      override.url,
      (tex) => {
        if (cancelled) { tex.dispose?.(); return }
        tex.mapping = THREE.EquirectangularReflectionMapping
        tex.colorSpace = THREE.SRGBColorSpace
        const rt = pmrem.fromEquirectangular(tex)
        tex.dispose?.()
        envTexture = rt.texture
        scene.environment = envTexture
        // Mirror onto every avatar material so envMap-on-material consumers
        // (e.g. MeshToonMaterial via userData) pick it up the same way they
        // would from EnvProbe.
        if (typeof window !== 'undefined' && window.__ENV_PROBE__) {
          window.__ENV_PROBE__ = { state: 'override', url: override.url, envMap: envTexture }
        }
      },
      undefined,
      (err) => console.warn('[HDRIApplier] load failed:', override.url, err),
    )

    return () => {
      cancelled = true
      pmrem.dispose()
      if (envTexture && scene.environment === envTexture) {
        scene.environment = null
      }
      envTexture?.dispose?.()
    }
  }, [override?.url, gl, scene])

  return null
}
